import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";
import { TagFormModal, DeleteTagModal } from "./TagModals";
import axios from "utils/axios";
import configFile from "config.json";
import { NotificationManager } from "react-notifications";

const TAG_API_BASE = `${configFile.PROD_SERVER_URL}/tag`;

const Tags = () => {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [categories, setCategories] = useState([])
  const [editingTag, setEditingTag] = useState(null);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    sort: "label_asc",
  });

  useEffect(() => {
    fetchTags();
    fetchCategories();
  }, []);

  const sortOptions = [
    { label: "Түлхүүр үг (A → Z)", value: "label_asc" },
    { label: "Түлхүүр үг (Z → A)", value: "label_desc" },
    { label: "Үүссэн огноо (Шинэ)", value: "created_desc" },
    { label: "Үүссэн огноо (Хуучин)", value: "created_asc" },
    { label: "Өөрчилсөн огноо (Шинэ)", value: "updated_desc" },
    { label: "Өөрчилсөн огноо (Хуучин)", value: "updated_asc" },
  ];

  const filteredTags = useMemo(() => {
    const result = tags.filter((tag) => {
      const matchSearch = tag.slug
        .toLowerCase()
        .includes(filters.search.toLowerCase());
      const matchCategory =
        filters.category === "all" || tag.category === filters.category;
      return (
        matchSearch &&
        matchCategory
      );
    });
    const sorted = [...result];
    switch (filters.sort) {
      case "label_desc":
        sorted.sort((a, b) => b.slug.localeCompare(a.slug));
        break;
      case "created_desc":
        sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
        break;
      case "created_asc":
        sorted.sort((a, b) => a.created_at.localeCompare(b.created_at));
        break;
      case "updated_desc":
        sorted.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
        break;
      case "updated_asc":
        sorted.sort((a, b) => a.updated_at.localeCompare(b.updated_at));
        break;
      default:
        sorted.sort((a, b) => a.slug.localeCompare(b.slug));
        break;
    }
    return sorted;
  }, [filters, tags]);

  const handleOpenCreate = () => {
    setEditingTag(null);
    form.resetFields();
    form.setFieldsValue({
      color: "#1677ff",
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (tag) => {
    setEditingTag(tag);
    form.setFieldsValue(tag);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      let is_success = false
      if (editingTag) {
        const response = await updateTag(editingTag.id, values);
        is_success = response?.data?.success
      } else {
        const response = await createTag({ label: values.slug, ...values });
        is_success = response?.data?.success
      }
      if (is_success) {
        fetchTags()
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingTag(null);
    } catch (error) {
      console.log(error);
      NotificationManager.error(error?.response?.data?.errorMessage || "Алдаа гарлаа")
    }
  };


  const handleDelete = (tag) => {
    setDeleteTarget(tag);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await deleteTag(deleteTarget.id);
      const isSuccess = response?.data?.success;

      if (!isSuccess) {
        NotificationManager.error(
          response?.data?.errorMessage || "Алдаа гарлаа"
        );
        return;
      }

      setTags((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setIsDeleteOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.log(error);
      NotificationManager.error(
        error?.response?.data?.errorMessage || "Алдаа гарлаа"
      );
    }
  };

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const result = await axios.post(`${TAG_API_BASE}/getAllTags`);
      const rows = result?.data?.data || result?.data || [];
      setTags(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const result = await axios.post(`${TAG_API_BASE}/getTagCategories`);
      const rows = result?.data?.data || result?.data || [];
      console.log(result)
      const formatted_rows = []
      for (const key of Object.keys(rows)) {
        formatted_rows.push({ label: key, value: rows[key] })
      }
      setCategories(formatted_rows);
    } catch (error) {
      console.log(error);
    }
  };

  const createTag = async (payload) => {
    return axios.post(`${TAG_API_BASE}/createTag`, payload);
  };

  const updateTag = async (id, payload) => {
    return axios.post(`${TAG_API_BASE}/updateTag`, { id, ...payload });
  };

  const deleteTag = async (id) => {
    return axios.post(`${TAG_API_BASE}/deleteTag`, { id });
  };

  const columns = [
    {
      title: "Түлхүүр үг",
      dataIndex: "slug",
      render: (_, record) => (
        <Space>
          <Tag color={record.color} style={{ marginRight: 0 }}>
            {record.slug}
          </Tag>
          {record.label && (
            <Tooltip title={record.label}>
              <span className="muted"><InfoCircleOutlined /></span>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Ангилал",
      dataIndex: "category",
      render: (value) => categories.find((opt) => opt.value === value)?.value,
    },
    {
      title: "Үүссэн огноо",
      dataIndex: "created_at",
      render: (value, record) => (
        <div>
          <div>{value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-"}</div>
          <span className="muted">{record.created_by}</span>
        </div>
      ),
    },
    {
      title: "Өөрчилсөн огноо",
      dataIndex: "updated_at",
      render: (value, record) => (
        <div>
          <div>{value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-"}</div>
          <span className="muted">{record.updated_by}</span>
        </div>
      ),
    },
    {
      title: "Үйлдэл",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleOpenEdit(record)}>
            <EditOutlined />
          </Button>
          <Button danger size="small" onClick={() => handleDelete(record)}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Container>
      <div className="page-header">
        <div>
          <h2>Tags</h2>
          <p className="muted">
            Tag-уудыг хайх, үүсгэх, өөрчлөх болон устгах
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Tag
        </Button>
      </div>

      <div className="filters">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Түлхүүр үгээр хайх"
          value={filters.search}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, search: event.target.value }))
          }
        />
        <Select
          value={filters.category}
          onChange={(value) => setFilters((prev) => ({ ...prev, category: value }))}
          options={[{ label: "Бүгд", value: "all" }, ...categories]}
        />
        <Select
          value={filters.sort}
          onChange={(value) => setFilters((prev) => ({ ...prev, sort: value }))}
          options={sortOptions}
        />
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredTags}
        loading={isLoading}
        pagination={false}
      />

      <TagFormModal
        title={editingTag ? "Tag өөрчлөх" : "Tag үүсгэх"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSave}
        okText={editingTag ? "Хадгалах" : "Үүсгэх"}
        form={form}
        categoryOptions={categories}
      />
      <DeleteTagModal
        open={isDeleteOpen}
        tag={deleteTarget}
        onCancel={() => {
          setIsDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

const Container = styled.div`
  h2 {
    margin: 0;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 18px 22px;
    border-radius: 8px;
    border: 1px solid rgba(22, 119, 255, 0.18);
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 18px 50px rgba(33, 49, 63, 0.1);
    backdrop-filter: blur(8px);
  }

  .page-header h2 {
    font-size: 22px;
    color: #1f2a33;
  }

  .page-header p {
    color: #6b7a88;
  }

  .filters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }

  .color-input {
    width: 120px;
    padding: 0;
  }

  .color-item .ant-input {
    width: 120px;
  }

  .swatch {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid #e5e7eb;
  }

  .muted {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
`;

export default Tags;
